// Modelo de datos del Sistema AVA.
// Estas interfaces reflejan 1:1 las tablas previstas en Supabase.
// Toda entidad de negocio lleva `organizationId` para aislar cada empresa (RLS).

export type UUID = string

/* ------------------------------------------------------------------ */
/* Usuarios, organizaciones y roles                                    */
/* ------------------------------------------------------------------ */

export type Role = 'propietario' | 'administrador' | 'comercial' | 'lectura'

export const ROLE_LABELS: Record<Role, string> = {
  propietario: 'Propietario',
  administrador: 'Administrador',
  comercial: 'Comercial',
  lectura: 'Solo lectura',
}

export interface User {
  id: UUID
  firstName: string
  lastName: string
  email: string
  avatarUrl?: string
  createdAt: string
}

export interface Organization {
  id: UUID
  name: string
  sector: string
  slug: string
  logoUrl?: string
  email?: string
  phone?: string
  website?: string
  address?: string
  timezone: string
  brand: OrganizationBrand
  privacy: OrganizationPrivacy
  notifications: OrganizationNotifications
  createdAt: string
}

export interface OrganizationBrand {
  primaryColor: string
  secondaryColor: string
  fontFamily: string
  logoUrl?: string
  faviconUrl?: string
  /* Ampliación de marca (todas opcionales para no romper datos existentes) */
  buttonColor?: string
  textColor?: string
  backgroundColor?: string
  secondaryFontFamily?: string
  domain?: string
}

/** Estilo visual editable de cualquier bloque de una landing. */
export interface BlockStyle {
  background?: 'transparente' | 'blanco' | 'gris' | 'marca' | 'oscuro'
  align?: 'left' | 'center' | 'right'
  paddingY?: number
  paddingX?: number
  radius?: number
  shadow?: 'ninguna' | 'suave' | 'media' | 'fuerte'
  maxWidth?: 'estrecho' | 'normal' | 'ancho' | 'completo'
  /** Tamaño del título en escritorio (px). Vacío = valor del diseño. */
  titleSize?: number
  /** Tamaño del texto en escritorio (px). Vacío = valor del diseño. */
  bodySize?: number
  titleWeight?: number
  letterSpacing?: number
  uppercase?: boolean
  textColor?: string
  /**
   * Ajustes que solo se aplican en móvil (≤ 640 px). Con `sync` activado se
   * usan los valores de escritorio; al desvincularlo, estos sustituyen solo la
   * versión móvil sin tocar escritorio ni tablet.
   */
  mobile?: MobileBlockStyle
  /** Igual que `mobile`, pero aplicado solo entre 641 y 1024 px. */
  tablet?: MobileBlockStyle
}

/** Tipografía y espaciado específicos de móvil para un bloque. */
export interface MobileBlockStyle {
  /** true = usar los mismos valores en todos los dispositivos. */
  sync?: boolean
  titleSize?: number
  bodySize?: number
  lineHeight?: number
  letterSpacing?: number
  titleWeight?: number
  align?: 'left' | 'center' | 'right'
  paddingY?: number
  paddingX?: number
}

/** Estilo editable de los botones de un bloque. */
export interface BlockButtonStyle {
  label?: string
  color?: string
  hoverColor?: string
  textColor?: string
  radius?: number
  height?: number
  paddingX?: number
  fullWidth?: boolean
  icon?: string
  shadow?: boolean
  animation?: 'ninguna' | 'elevar' | 'escalar' | 'pulso'
}

/** Componente guardado por la empresa para reutilizarlo en otras landings. */
export interface SavedComponent {
  id: UUID
  organizationId: UUID
  name: string
  category: string
  block: LandingBlock
  createdAt: string
}

export interface OrganizationPrivacy {
  privacyPolicyUrl: string
  legalNoticeUrl: string
  consentText: string
  cookiesText: string
}

export interface OrganizationNotifications {
  newLead: boolean
  leadAssigned: boolean
  statusChange: boolean
  newMeeting: boolean
}

export interface OrganizationMember {
  id: UUID
  organizationId: UUID
  userId: UUID
  role: Role
  active: boolean
  firstName: string
  lastName: string
  email: string
  avatarUrl?: string
  createdAt: string
}

export interface Invitation {
  id: UUID
  organizationId: UUID
  email: string
  role: Role
  status: 'pendiente' | 'aceptada' | 'cancelada'
  createdAt: string
}

/* ------------------------------------------------------------------ */
/* Formularios                                                          */
/* ------------------------------------------------------------------ */

export type QuestionType =
  | 'texto_corto'
  | 'texto_largo'
  | 'email'
  | 'telefono'
  | 'numero'
  | 'url'
  | 'seleccion_unica'
  | 'seleccion_multiple'
  | 'desplegable'
  | 'fecha'
  | 'hora'
  | 'si_no'
  | 'casilla'
  | 'archivo'
  | 'imagen'

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  texto_corto: 'Texto corto',
  texto_largo: 'Texto largo',
  email: 'Email',
  telefono: 'Teléfono',
  numero: 'Número',
  url: 'URL',
  seleccion_unica: 'Selección única',
  seleccion_multiple: 'Selección múltiple',
  desplegable: 'Desplegable',
  fecha: 'Fecha',
  hora: 'Hora',
  si_no: 'Sí o no',
  casilla: 'Casilla de aceptación',
  archivo: 'Archivo adjunto',
  imagen: 'Imagen',
}

export type ConditionalOperator =
  | 'igual'
  | 'distinto'
  | 'contiene'
  | 'no_contiene'
  | 'respondida'
  | 'no_respondida'

export const OPERATOR_LABELS: Record<ConditionalOperator, string> = {
  igual: 'Es igual a',
  distinto: 'No es igual a',
  contiene: 'Contiene',
  no_contiene: 'No contiene',
  respondida: 'Está respondida',
  no_respondida: 'No está respondida',
}

export interface ConditionalLogic {
  enabled: boolean
  questionId: UUID | null
  operator: ConditionalOperator
  value: string
}

export interface FormQuestion {
  id: UUID
  formId: UUID
  label: string
  description?: string
  type: QuestionType
  options: string[]
  required: boolean
  order: number
  placeholder?: string
  /** Texto secundario bajo cada opción (mismo orden que `options`). */
  optionDescriptions?: string[]
  /** Columna del CRM donde se vuelca la respuesta. */
  crmField?: 'service' | 'adBudget' | 'budget' | 'city' | 'instagram'
  /** Columna real de `leads` donde se guarda la respuesta (`field_key`). */
  fieldKey?: string
  /** Valor guardado en el CRM por cada opción (mismo orden que `options`). */
  crmValues?: string[]
  conditionalLogic: ConditionalLogic
  isActive: boolean
}

export interface FormContactField {
  key: string
  label: string
  type: QuestionType
  required: boolean
  placeholder?: string
}

export interface Form {
  id: UUID
  organizationId: UUID
  name: string
  description?: string
  slug: string
  status: 'borrador' | 'activo' | 'inactivo'
  submitLabel: string
  successMessage: string
  thankYouSlug?: string
  redirectUrl?: string
  contactFields: FormContactField[]
  /** Posición del bloque de contacto: al inicio (por defecto) o al final. */
  contactPosition?: 'inicio' | 'final'
  /** Nombre del paso de contacto, mostrado junto al progreso. */
  contactTitle?: string
  /** Texto del botón de avance. Por defecto «Siguiente». */
  nextLabel?: string
  /** Texto del botón de retroceso. Por defecto «Anterior». */
  backLabel?: string
  /**
   * Texto informativo (NO bloqueante) que se muestra al pie del formulario.
   * Sustituye a la antigua casilla obligatoria de consentimiento.
   */
  privacyNotice?: {
    enabled: boolean
    text: string
    linkLabel?: string
    linkUrl?: string
  }
  consentText: string
  /**
   * Qué ocurre cuando el visitante termina el formulario. El lead SIEMPRE se
   * guarda antes de ejecutar la acción.
   */
  postSubmit?: PostSubmitConfig
  /**
   * Recuperar el paso y las respuestas si el visitante vuelve. Desactivado por
   * defecto: cada visita empieza siempre en el primer paso.
   */
  saveProgress?: boolean
  questions: FormQuestion[]
  createdAt: string
  updatedAt: string
  /** Borrado lógico. */
  deletedAt?: string
}

/** Acción posterior al envío del formulario. */
export type PostSubmitAction = 'gracias' | 'whatsapp' | 'url' | 'mensaje'

export interface PostSubmitConfig {
  action: PostSubmitAction
  /** Número de WhatsApp con prefijo internacional, p. ej. +34600123456. */
  whatsappNumber?: string
  /** Mensaje predeterminado, admite variables {{full_name}}, {{company}}… */
  whatsappMessage?: string
  /** Abrir WhatsApp automáticamente al enviar, sin pulsar el botón. */
  whatsappAuto?: boolean
  whatsappLabel?: string
  /** URL externa a la que redirigir (Kajabi, Calendly, web de venta…). */
  redirectUrl?: string
  messageTitle?: string
  messageText?: string
}

export interface FormAnswer {
  questionId: UUID
  label: string
  value: string | string[]
}

export interface FormSubmission {
  id: UUID
  organizationId: UUID
  formId: UUID
  landingId?: UUID
  leadId: UUID
  answers: FormAnswer[]
  createdAt: string
}

/* ------------------------------------------------------------------ */
/* Landings                                                            */
/* ------------------------------------------------------------------ */

export type LandingBlockType =
  | 'topbar'
  | 'logo'
  | 'hero'
  | 'titulo'
  | 'subtitulo'
  | 'imagen'
  | 'video'
  | 'beneficios'
  | 'estadisticas'
  | 'testimonios'
  | 'logos_clientes'
  | 'formulario'
  | 'faq'
  | 'calendario'
  | 'whatsapp'
  | 'cta_final'
  | 'footer'
  | 'pilares'
  | 'frase'
  | 'confianza'
  | 'garantias'
  | 'servicios'
  | 'caracteristicas'
  | 'galeria'
  | 'precios'
  | 'contacto'
  /** Comparativa «Es para ti / No es para ti». Nunca visible por defecto. */
  | 'filtro'
  /** Historia o «Sobre mí» con foto. Nunca visible por defecto. */
  | 'historia'

export interface LandingBlock {
  id: UUID
  type: LandingBlockType
  visible: boolean
  spacing: 'compacto' | 'normal' | 'amplio'
  // Configuración específica de cada bloque (textos, listas, urls...)
  config: Record<string, unknown>
  /** Estilo visual editable desde el constructor. */
  style?: BlockStyle
  /** Estilo del botón principal del bloque. */
  button?: BlockButtonStyle
}

export type LandingTheme = 'blanco_premium' | 'claro_corporativo' | 'minimalista'

/** Dispositivos en los que puede mostrarse un elemento flotante. */
export type FloatingDevice = 'desktop' | 'tablet' | 'mobile'

export interface FloatingWhatsapp {
  enabled: boolean
  phone?: string
  /** Mensaje con el que se abre la conversación. */
  message?: string
  /** Texto junto al icono; vacío = solo icono. */
  label?: string
  /** Tarjeta previa con una invitación a escribir. */
  showCard?: boolean
  cardTitle?: string
  cardText?: string
  position?: 'derecha' | 'izquierda'
  /** Diámetro del botón en píxeles (40–72). */
  size?: number
  devices?: FloatingDevice[]
}

export interface FloatingMessage {
  title: string
  text?: string
  time?: string
}

export interface FloatingActivity {
  enabled: boolean
  /** `real` usa la actividad del sistema; `manual` los mensajes definidos aquí. */
  mode?: 'manual' | 'real'
  messages?: FloatingMessage[]
  position?: 'derecha' | 'izquierda'
  /** Segundos hasta el primer aviso. */
  delay?: number
  /** Segundos que permanece visible. */
  duration?: number
  /** Segundos entre avisos. */
  interval?: number
  /** Número máximo de apariciones por visita. */
  max?: number
  showTime?: boolean
  devices?: FloatingDevice[]
}

/** Elementos flotantes opcionales, guardados en cada landing por separado. */
export interface FloatingConfig {
  whatsapp?: FloatingWhatsapp
  activity?: FloatingActivity
}

/** Efectos de fondo opcionales. `normal` = sin efecto (valor por defecto). */
export type LandingBackgroundType =
  | 'normal'
  /** Chispas Tech — Minimal / Neural / Aura. */
  | 'chispas_minimal'
  | 'chispas_neural'
  | 'chispas_aura'
  /** Efectos anteriores: se conservan para las landings que ya los usaban. */
  | 'destellos'
  | 'red'
  | 'aura'
  | 'grid'

export interface LandingBackground {
  type: LandingBackgroundType
  intensity?: 'baja' | 'media'
  /** Cantidad de partículas. `baja` por defecto. */
  density?: 'baja' | 'media' | 'alta'
  speed?: 'muy_lenta' | 'lenta'
  /** Opacidad del efecto en porcentaje (2–20). */
  opacity?: number
  /** false deja el efecto estático, sin animación. */
  motion?: boolean
  devices?: FloatingDevice[]
}

export interface LandingPageConfig {
  theme: LandingTheme
  primaryColor: string
  fontFamily: string
  logoUrl?: string
  blocks: LandingBlock[]
  thankYou: ThankYouConfig
  /** Botón de WhatsApp y avisos de actividad. */
  floating?: FloatingConfig
  /** Efecto de fondo opcional. Por defecto `normal` (sin efecto). */
  background?: LandingBackground
  /** Identificación estable de la plantilla oficial (`ava_original_vsl`). */
  templateKey?: string
  /** Versión de la plantilla ya aplicada a esta landing. */
  templateVersion?: number
  /** Meta Pixel (Facebook/Instagram). Por defecto usa el Pixel global de la organización. */
  metaPixel?: LandingMetaPixel
}

/** Eventos de Meta Pixel que puede disparar una landing. */
export interface MetaPixelEvents {
  /** Al entrar en la landing pública. */
  pageView?: boolean
  /** Solo cuando el formulario se ha guardado correctamente. */
  lead?: boolean
  /** Al pulsar el botón de WhatsApp de la página de gracias. */
  whatsapp?: boolean
  /**
   * Reserva confirmada en el calendario. Sin forma fiable de detectarla hoy
   * (el bloque de calendario admite cualquier URL de agenda externa), así que
   * viene desactivado y no se dispara desde ningún sitio todavía.
   */
  schedule?: boolean
}

/** Meta Pixel de una landing: por defecto hereda el Pixel global de la organización. */
export interface LandingMetaPixel {
  enabled?: boolean
  /** Solo dígitos. Un valor con formato inválido se descarta y no se carga ningún script. */
  pixelId?: string
  events?: MetaPixelEvents
  /** true = usar los valores de aquí; false/ausente = usar el Pixel global de la organización. */
  override?: boolean
}

export interface ThankYouConfig {
  title: string
  /** Subtítulo opcional sobre el texto principal. */
  subtitle?: string
  text: string
  imageUrl?: string
  /** Bloque de vídeo opcional de la página de gracias. */
  showVideo?: boolean
  videoUrl?: string
  videoThumbnailUrl?: string
  showCalendly: boolean
  calendlyUrl?: string
  showWhatsapp: boolean
  whatsappNumber?: string
  /**
   * Plantilla del mensaje que se abre ya escrito en WhatsApp. Admite variables
   * ({{nombre}}, {{empresa}}, {{ciudad}}…) que se rellenan con las respuestas
   * del envío que acaba de hacer ESE visitante. Las líneas cuyas variables
   * queden vacías se eliminan.
   */
  whatsappMessage?: string
  /** Título y textos del paso de WhatsApp, editables desde el constructor. */
  whatsappEyebrow?: string
  whatsappStepTitle?: string
  whatsappStepText?: string
  whatsappLabel?: string
  /** Mostrar la vista previa del mensaje bajo el botón. */
  showWhatsappPreview?: boolean
  /** Expectativa de respuesta («… en un plazo máximo de 24 horas»). */
  replyNote?: string
  showReplyNote?: boolean
  showHome: boolean
  autoRedirectUrl?: string
}

export interface LandingPage {
  id: UUID
  organizationId: UUID
  formId?: UUID
  name: string
  slug: string
  status: 'borrador' | 'publicada' | 'despublicada'
  pageConfig: LandingPageConfig
  views: number
  createdAt: string
  updatedAt: string
  /** Borrado lógico. */
  deletedAt?: string
}

/* ------------------------------------------------------------------ */
/* CRM                                                                  */
/* ------------------------------------------------------------------ */

export interface PipelineStage {
  id: UUID
  organizationId: UUID
  name: string
  color: string
  order: number
  active: boolean
  isInitial: boolean
}

export interface LeadOrigin {
  source: string
  medium: string
  campaign: string
  content: string
  term: string
  landingId?: UUID
  formId?: UUID
  referrer: string
}

export interface Lead {
  id: UUID
  organizationId: UUID
  firstName: string
  lastName: string
  company: string
  email: string
  phone: string
  website?: string
  /* Campos comerciales adicionales (opcionales) */
  instagram?: string
  city?: string
  country?: string
  service?: string
  adBudget?: string
  budget?: string
  stageId: UUID
  assignedTo?: UUID
  origin: LeadOrigin
  answers: FormAnswer[]
  createdAt: string
  lastContactAt?: string
  /** Borrado lógico: el lead pasa a la papelera durante 30 días. */
  deletedAt?: string
  deletedBy?: string
}

/** Color visual de una nota interna. */
export type NoteColor = 'gray' | 'blue' | 'green' | 'red'

export interface LeadNote {
  id: UUID
  organizationId: UUID
  leadId: UUID
  authorId: UUID
  authorName: string
  title?: string
  body: string
  /** Color de la nota. Por defecto `gray`. */
  color: NoteColor
  createdAt: string
  updatedAt?: string
  /** Borrado lógico. */
  deletedAt?: string
  deletedBy?: string
}

/** Fuente de captación editable por cada organización. */
export interface LeadSource {
  id: UUID
  organizationId: UUID
  /** Clave estable usada al detectar la fuente automáticamente. */
  key: string
  name: string
  icon: string
  color: string
  active: boolean
  order: number
}

/** Archivos adjuntos a un lead (se resolverán con Supabase Storage). */
export interface LeadFile {
  id: UUID
  organizationId: UUID
  leadId: UUID
  name: string
  size: number
  type: string
  url: string
  uploadedBy: string
  createdAt: string
}

export interface LeadStatusHistory {
  id: UUID
  organizationId: UUID
  leadId: UUID
  type: 'creacion' | 'estado' | 'asignacion' | 'nota' | 'contacto'
  description: string
  authorName: string
  createdAt: string
}

/* ------------------------------------------------------------------ */
/* Integraciones                                                       */
/* ------------------------------------------------------------------ */

export interface Integration {
  id: string
  name: string
  description: string
  status: 'no_conectado' | 'proximamente'
  icon: string
}
